/**
 * Conversion helpers between the Text Notes `document_config.content` Firestore
 * shape and the editable form shape. Rows get stable `id`s for React keys /
 * inline editing; they are stripped on the way back to Firestore. Unknown
 * top-level content keys are preserved via the original content spread.
 */
import { v4 as uuidv4 } from "uuid";
import {
  TextNoteBodyColumn,
  TextNotesContent,
} from "../../../../../data-model/deliveryStepsConfig";
import { QueryGroup } from "../../../../../data-model/queryBuilder";
import { queryValueToWhen, whenToQueryValue } from "./summaryWhenConversion";

export interface BodyColumnRow {
  id: string;
  fieldName: string;
  displayLabel: string;
  formatTemplate: string;
  renderOrder: number | undefined;
  required: boolean;
  bqColumns: string[];
  fallbackSelect: string;
  /** Stored `what_we_add_label`, or "" when the column has none. */
  whatWeAddLabel: string;
  /** Column as loaded from Firestore; absent for rows added in this session. */
  original?: TextNoteBodyColumn;
}

export interface SummaryRuleRow {
  id: string;
  ruleId: string;
  valueTemplate: string;
  when: QueryGroup;
}

export interface TextNotesContentForm {
  bodyColumns: BodyColumnRow[];
  requiredStatsFields: string[];
  summaryEnabled: boolean;
  summaryFieldName: string;
  summarySourceColumns: string[];
  summaryRules: SummaryRuleRow[];
}

function bodyColumnToRow(col: TextNoteBodyColumn): BodyColumnRow {
  return {
    id: uuidv4(),
    fieldName: col.field_name ?? "",
    displayLabel: col.display_label ?? "",
    formatTemplate: col.format_template ?? "",
    renderOrder: col.render_order,
    required: col.required ?? false,
    bqColumns: col.bq_columns ?? [],
    fallbackSelect: col.fallback_select ?? "",
    whatWeAddLabel: col.what_we_add_label ?? "",
    original: col,
  };
}

/** Builds the editable content form from a `document_config.content` object. */
export function contentFromConfig(
  content: TextNotesContent | undefined,
): TextNotesContentForm {
  const summary = content?.summary_resolution;
  return {
    bodyColumns: (content?.text_note_body_columns ?? []).map(bodyColumnToRow),
    requiredStatsFields: content?.required_cmm_stats_fields ?? [],
    summaryEnabled: summary?.enabled ?? false,
    summaryFieldName: summary?.field_name ?? "",
    summarySourceColumns: summary?.source_columns ?? [],
    summaryRules: (summary?.rules ?? []).map((rule) => ({
      id: uuidv4(),
      ruleId: rule.id ?? "",
      valueTemplate: rule.value_template ?? "",
      when: whenToQueryValue(rule.when ?? {}),
    })),
  };
}

function rowToBodyColumn(row: BodyColumnRow): TextNoteBodyColumn {
  const column: TextNoteBodyColumn = {
    // Spread first so keys the UI never edits (e.g. `source`) survive a save.
    ...(row.original ?? {}),
    field_name: row.fieldName,
    display_label: row.displayLabel.trim() === "" ? null : row.displayLabel,
    format_template: row.formatTemplate,
    render_order: row.renderOrder ?? 0,
    required: row.required,
    bq_columns: row.bqColumns,
  };
  // Optional keys are dropped, not left stale, when the user clears them.
  if (row.fallbackSelect.trim() === "") delete column.fallback_select;
  else column.fallback_select = row.fallbackSelect;
  if (row.whatWeAddLabel.trim() === "") delete column.what_we_add_label;
  else column.what_we_add_label = row.whatWeAddLabel;
  return column;
}

/**
 * Rebuilds the `content` object from the form, preserving any unknown keys from
 * the original content. The caller round-trips through JSON to drop `undefined`.
 */
export function contentToConfig(
  form: TextNotesContentForm,
  original: TextNotesContent | undefined,
): TextNotesContent {
  return {
    ...(original ?? {}),
    text_note_body_columns: form.bodyColumns.map(rowToBodyColumn),
    html_formatting: original?.html_formatting,
    screenshot_config: original?.screenshot_config,
    required_cmm_stats_fields: form.requiredStatsFields,
    summary_resolution: {
      enabled: form.summaryEnabled,
      field_name: form.summaryFieldName,
      source_columns: form.summarySourceColumns,
      rules: form.summaryRules.map((rule) => ({
        id: rule.ruleId,
        value_template: rule.valueTemplate,
        when: queryValueToWhen(rule.when),
      })),
    },
  };
}
